import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileEdit,
  FolderKanban,
  FileText,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const RequirementGatheringLanding = () => {
  const navigate = useNavigate();

  const gatheringCards = [
    {
      id: 'project-initiation',
      title: 'Project Initiation',
      description: 'Kickoff the project, fill structured fields, invite contributors — AI analyses all inputs continuously.',
      icon: <FolderKanban size={24} />,
      path: '/sourcing/project-intelligence/initiation',
      features: ['Structured fields', 'Team contributions', 'AI brief'],
    },
    {
      id: 'dpr-builder',
      title: 'DPR Builder',
      description: 'AI drafts sections from the Project Brief. Review inline, comment per section, regenerate any section on demand.',
      icon: <FileEdit size={24} />,
      path: '/sourcing/project-intelligence/builder',
      features: ['6 AI-drafted sections', 'Inline commenting', 'Section regeneration'],
    },
    {
      id: 'regulatory-approvals',
      title: 'Regulatory Approvals',
      description: 'Track regulatory clearances and approvals with upload slots and due dates.',
      icon: <FileText size={24} />,
      path: '/sourcing/project-intelligence/regulatory',
      features: ['Compliance matrix', 'Document uploads', 'Due date tracking'],
    },
    {
      id: 'dpr-finalisation',
      title: 'DPR Finalisation',
      description: 'AI consolidates brief, sections, and annexures for Department Head approval.',
      icon: <CheckCircle size={24} />,
      path: '/sourcing/project-intelligence/finalisation',
      features: ['AI consolidation', '1-page executive summary', 'Final approval'],
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb */}
      <PageBreadcrumb />

      {/* Page Header */}
      <PageHeader
        title="Project Intelligence"
        description="Four sequential steps: initiate the project, build the DPR with AI, track regulatory approvals, then finalise."
      />

      <div className="page-container">
        {/* Cards */}
        <div
          className="grid-modern-2"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {gatheringCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              <div
                style={{
                  height: '4px',
                  background: 'var(--gold-primary)',
                  width: '100%',
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px',
                    }}
                  >
                    <div className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        margin: '0',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>

                {/* Description */}
                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    margin: '0 0 12px 0',
                    lineHeight: '1.4',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default RequirementGatheringLanding;
